'use client';

import * as React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from './Avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { PresenceParticipant } from '../endpoints/presence/list_GET.schema';
import styles from './AvatarWithStatus.module.css';

export type UserStatus = 'online' | 'meeting' | 'away' | 'offline';

interface AvatarWithStatusProps {
  participant?: PresenceParticipant;
  // Fallback props if participant is not provided
  displayName?: string;
  avatarUrl?: string | null;
  status?: UserStatus;
  color?: string;
  currentFile?: string | null;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
  onClick?: () => void;
  className?: string;
}

const STATUS_COLORS: Record<UserStatus, string> = {
  online: '#22c55e', // green
  meeting: '#f59e0b', // amber
  away: '#f97316', // orange
  offline: '#6b7280', // gray
};

const STATUS_LABELS: Record<UserStatus, string> = {
  online: 'Online',
  meeting: 'In a meeting',
  away: 'Away',
  offline: 'Offline',
};

export const AvatarWithStatus: React.FC<AvatarWithStatusProps> = ({
  participant,
  displayName: propDisplayName,
  avatarUrl: propAvatarUrl,
  status: propStatus,
  color: propColor,
  currentFile: propCurrentFile,
  size = 'md',
  showTooltip = true,
  onClick,
  className,
}) => {
  // Extract from participant or use props
  const displayName = participant?.displayName ?? propDisplayName ?? 'Unknown';
  const avatarUrl = participant?.avatarUrl ?? propAvatarUrl ?? null;
  const status = participant?.status ?? propStatus ?? 'offline';
  const color = participant?.color ?? propColor ?? '#6366f1';
  const currentFile = participant?.currentFile ?? propCurrentFile ?? null;
  const lastSeenAt = participant?.lastSeenAt;

  // Create initials for fallback
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  // Format last seen time
  const formatLastSeen = (date: Date | null) => {
    if (!date) return 'Unknown';
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const sizeClass = {
    sm: styles.sizeSm,
    md: styles.sizeMd,
    lg: styles.sizeLg,
  }[size];

  const avatarContent = (
    <div
      className={`${styles.avatarWrapper} ${sizeClass} ${className ?? ''}`}
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      <Avatar className={styles.avatar}>
        {avatarUrl && <AvatarImage src={avatarUrl} />}
        <AvatarFallback style={{ backgroundColor: color }}>{initials}</AvatarFallback>
      </Avatar>
      <span
        className={styles.statusDot}
        style={{ backgroundColor: STATUS_COLORS[status] }}
        title={STATUS_LABELS[status]}
      />
    </div>
  );

  if (!showTooltip) {
    return avatarContent;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{avatarContent}</TooltipTrigger>
      <TooltipContent className={styles.tooltipContent} side="bottom" align="center">
        <div className={styles.tooltipCard}>
          <div className={styles.tooltipHeader}>
            <Avatar className={styles.tooltipAvatar}>
              {avatarUrl && <AvatarImage src={avatarUrl} />}
              <AvatarFallback style={{ backgroundColor: color }}>{initials}</AvatarFallback>
            </Avatar>
            <div className={styles.tooltipInfo}>
              <span className={styles.tooltipName}>{displayName}</span>
              <span className={styles.tooltipStatus} style={{ color: STATUS_COLORS[status] }}>
                {STATUS_LABELS[status]}
              </span>
            </div>
          </div>
          {currentFile && (
            <div className={styles.tooltipDetail}>
              <span className={styles.detailLabel}>Editing:</span>
              <span className={styles.detailValue}>{currentFile.split('/').pop()}</span>
            </div>
          )}
          {status !== 'online' && lastSeenAt && (
            <div className={styles.tooltipDetail}>
              <span className={styles.detailLabel}>Last seen:</span>
              <span className={styles.detailValue}>{formatLastSeen(lastSeenAt)}</span>
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

// Stack of multiple avatars with overflow indicator
interface AvatarStackProps {
  participants: PresenceParticipant[];
  maxVisible?: number;
  size?: 'sm' | 'md' | 'lg';
  onAvatarClick?: (participant: PresenceParticipant) => void;
}

export const AvatarStack: React.FC<AvatarStackProps> = ({
  participants,
  maxVisible = 4,
  size = 'sm',
  onAvatarClick,
}) => {
  const visibleParticipants = participants.slice(0, maxVisible);
  const overflowCount = participants.length - maxVisible;

  return (
    <div className={styles.avatarStack}>
      {visibleParticipants.map((participant, index) => (
        <div
          key={participant.userId}
          className={styles.stackItem}
          style={{ zIndex: visibleParticipants.length - index }}
        >
          <AvatarWithStatus
            participant={participant}
            size={size}
            onClick={() => onAvatarClick?.(participant)}
          />
        </div>
      ))}
      {overflowCount > 0 && (
        <div className={`${styles.stackItem} ${styles.overflowBadge}`}>
          <span>+{overflowCount}</span>
        </div>
      )}
    </div>
  );
};
